/**
 * Scrolling text section:
 * - auto: marquee with duration scaled to content width (consistent px/s per speed setting)
 * - scroll: horizontal movement tied to page scroll
 */
class ScrollingTextSection extends HTMLElement {
  #frame = null;
  #resizeObserver = null;
  #onResize = null;

  connectedCallback() {
    if (this.dataset.mode === 'scroll') {
      this.#initScrollMode();
      return;
    }

    if (this.dataset.mode === 'auto') {
      this.#initAutoMode();
    }
  }

  disconnectedCallback() {
    if (this.#onResize) {
      window.removeEventListener('resize', this.#onResize);
    }

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
  }

  #initScrollMode() {
    this.#onScroll = this.#onScroll.bind(this);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#onScroll();
  }

  #initAutoMode() {
    this.#updateAutoDuration();

    this.#onResize = () => {
      if (this.#frame !== null) return;

      this.#frame = requestAnimationFrame(() => {
        this.#frame = null;
        this.#updateAutoDuration();
      });
    };

    window.addEventListener('resize', this.#onResize, { passive: true });

    const track = this.querySelector('[data-scrolling-text-track]');
    if (track && typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#onResize());
      this.#resizeObserver.observe(track);
    }
  }

  #onScroll() {
    if (this.#frame !== null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#updateScrollPosition();
    });
  }

  #updateScrollPosition() {
    const track = this.querySelector('[data-scrolling-text-track]');
    if (!track) return;

    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight;
    const scrollable = rect.height + viewport;

    if (scrollable <= 0) return;

    const progress = (viewport - rect.top) / scrollable;
    const clamped = Math.min(1, Math.max(0, progress));
    const maxOffset = Math.max(0, track.scrollWidth - this.clientWidth);

    track.style.transform = `translate3d(${-clamped * maxOffset}px, 0, 0)`;
  }

  #updateAutoDuration() {
    const track = this.querySelector('[data-scrolling-text-track]');
    if (!track) return;

    const host =
      this.closest('[id^="shopify-section-"]') ??
      this.parentElement ??
      this;

    const speedSetting = Number.parseFloat(
      getComputedStyle(host).getPropertyValue('--st-duration')
    );

    if (!Number.isFinite(speedSetting) || speedSetting <= 0) return;

    const halfWidth = track.scrollWidth / 2;
    if (halfWidth <= 0) return;

    // scrolling_speed maps to px/s so short and long text feel the same at the same setting.
    const pixelsPerSecond = speedSetting * 8;
    const duration = halfWidth / pixelsPerSecond;

    track.style.animationDuration = `${duration}s`;
  }
}

if (!customElements.get('scrolling-text-section')) {
  customElements.define('scrolling-text-section', ScrollingTextSection);
}
